import { Employee } from '../interfaces/interface';
import { logger, handleError } from '../../../../logger';
import { LEADER_EA_EMAIL_DATA } from '../constants/EAHuddleConstants';
import { getEmployeeByLogin, getEmployeeById } from '../../../../api/papi-service';
import { PapiEmployeeMapper } from '../utils/papiEmployeeMapper';

/**
 * Service for handling employee data and operations
 * Uses PAPI to fetch real-time executive assistant information
 * Now works with provided employee data rather than API-based initialization
 */
export class EmployeeService {
  static async findExecutiveAssistant(leader: Employee): Promise<Employee | null> {
    try {
      // Get leader's PAPI data to retrieve the assistant's person ID
      const leaderResponse = await getEmployeeByLogin(leader.login_name);

      if (!leaderResponse.employee) {
        logger.warn(`Leader ${leader.login_name} not found in PAPI`);
        return null;
      }

      // Extract the assistant's person ID
      // Note: assistantPersonId only exists in FintechPapiJob, not in FintechPAPISearchJob
      const job = leaderResponse.employee.job;
      const assistantPersonId = job && 'assistantPersonId' in job ? job.assistantPersonId : undefined;

      if (!assistantPersonId) {
        logger.info(`No executive assistant assigned for ${leader.employee_name}`);
        return null; // No EA assigned
      }

      // Get EA details using the person ID
      const eaResponse = await getEmployeeById(assistantPersonId);

      if (!eaResponse.employee) {
        logger.warn(`Executive assistant with person ID ${assistantPersonId} not found in PAPI`);
        return null;
      }

      // Map PAPI employee data to Employee interface
      const executiveAssistant = PapiEmployeeMapper.mapToEmployee(eaResponse.employee);

      // Check if leader's alias exists in constant data and override EA email if available
      const leaderInConstant = LEADER_EA_EMAIL_DATA.find((leaderData) => leaderData.alias.toLowerCase() === leader.login_name.toLowerCase());

      const eaEmail = leaderInConstant?.eaEmailAddress || executiveAssistant.employee_internal_email_address;

      return {
        ...executiveAssistant,
        employee_internal_email_address: eaEmail
      };
    } catch (error) {
      handleError(`Error during executive assistant lookup for ${leader.employee_name}`, error);
      return null;
    }
  }
}
